import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { ParcelsWithProperties } from '@core/api/angular';
import { isEmptyInputStr, unique } from '@lassi/util';
import {
  DisallowedValuesPipe,
  TransformCrownStatusPipe,
  TransformParcelStatusPipe,
  TransformZLevelPipe,
} from '@lassi/webapp-shared-pipes';
import { ParcelPropertyGridComponent } from './parcel-property-grid.component';
import { ParcelPropertyTableComponent } from './parcel-property-table.component';

@Component({
  selector: 'lib-parcel-display',
  imports: [
    MatDividerModule,
    TransformCrownStatusPipe,
    DisallowedValuesPipe,
    TransformZLevelPipe,
    DisallowedValuesPipe,
    ParcelPropertyTableComponent,
    ParcelPropertyGridComponent,
  ],
  template: `
    @let isCrownDescription = parcel().desc_type === CROWN_DESC_TYPE;
    @let parcelZLevel = parcel().z_level | transformZLevel;
    @let parcelType =
      (isCrownDescription ? 'Crown Description' : 'Lot / Plan') +
      (parcel().z_level === 'G' ? '' : ' (' + parcelZLevel.name + ')');

    <h3 class="mat-title-medium">
      {{ prefix() }}{{ parcelType }}{{ parcelStatus() }}
    </h3>

    <div class="identify-grid">
      @if (!isCrownDescription) {
        <!-- Lot/Plan specific top fields -->
        @if (parcel().lot_number | disallowedValues: ['N', null, '']) {
          <span class="mat-body-small">Lot:</span>
          <span class="mat-body-small">{{ parcel().lot_number }}</span>
        }

        @if (parcel().plan_number | disallowedValues: ['N', null, '']) {
          <span class="mat-body-small">Plan:</span>
          <span class="mat-body-small">{{ parcel().plan_number }}</span>
        }
      }

      @if (isCrownDescription) {
        <!-- Crown Description specific top field -->
        @if (parcel().allotment | disallowedValues: ['N', null, '']) {
          <span class="mat-body-small">Allotment:</span>
          <span class="mat-body-small">{{ parcel().allotment }}</span>
        }
      }

      <!-- Shared: Section, Block, Portion, Subdivision -->
      @if (parcel().sec | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Section:</span>
        <span class="mat-body-small">{{ parcel().sec }}</span>
      }

      @if (parcel().block | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Block:</span>
        <span class="mat-body-small">{{ parcel().block }}</span>
      }

      @if (parcel().portion | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Portion:</span>
        <span class="mat-body-small">{{ parcel().portion }}</span>
      }

      @if (parcel().subdivision | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Subdivision:</span>
        <span class="mat-body-small">{{ parcel().subdivision }}</span>
      }

      @if (isCrownDescription) {
        <!-- Crown Description specific middle fields -->
        @if (parcel().parish_code | disallowedValues: ['N', null, '', '9999']) {
          <span class="mat-body-small">Parish Code:</span>
          <span class="mat-body-small">{{ parcel().parish_code }}</span>
        }

        @if (
          parcel().township_code | disallowedValues: ['N', null, '', '9999']
        ) {
          <span class="mat-body-small">Township Code:</span>
          <span class="mat-body-small">{{ parcel().township_code }}</span>
        }
      }

      <!-- Shared remaining fields -->
      @let parcelSpi = parcel().spi;
      @if (parcelSpi | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">SPI:</span>
        <span
          class="mat-body-small spi-field"
          [innerHTML]="displayedSpi()"
        ></span>
      }

      @if (parcel().spi_code) {
        <span class="mat-body-small">SPI Code:</span>
        <span class="mat-body-small">{{ parcel().spi_code }}</span>
      }

      @if (parcel().further_description | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">F.Desc:</span>
        <span class="mat-body-small">{{ parcel().further_description }}</span>
      }

      @if (parcel().crown_status | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Crown Status:</span>
        <span class="mat-body-small">{{
          parcel().crown_status | transformCrownStatus
        }}</span>
      }

      @if (parcel().parcel_pfi | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">PFI:</span>
        <span class="mat-body-small">{{ parcel().parcel_pfi }}</span>
      }

      @if (parcel().parcel_view_pfi | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">ViewPFI:</span>
        <span class="mat-body-small">{{ parcel().parcel_view_pfi }}</span>
      }

      @if (parcel().desc_type | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">DescType:</span>
        <span class="mat-body-small">{{ parcel().desc_type }}</span>
      }

      @if (parcel().z_level | disallowedValues: ['N', null, '', 'G']) {
        <span class="mat-body-small">Z Levels:</span>
        @let zLevel = parcel().z_level | transformZLevel;
        <span class="mat-body-small">
          {{ zLevel.name }}
          @if (zLevel.count) {
            ({{ zLevel.count }})
          }
        </span>
      }

      @if (parcel().multi | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Is Multi:</span>
        <span class="mat-body-small">{{ parcel().multi }}</span>
      }

      @if (parcel().road | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Is Road:</span>
        <span class="mat-body-small">{{ parcel().road }}</span>
      }

      @if (planningZone() | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Planning Zone:</span>
        <span class="mat-body-small">{{ planningZone() }}</span>
      }

      @if (planningOverlay() | disallowedValues: ['N', null, '']) {
        <span class="mat-body-small">Planning Overlay:</span>
        <span class="mat-body-small">{{ planningOverlay() }}</span>
      }
    </div>

    <div class="properties-container">
      <div class="table-view">
        <lib-parcel-property-table
          [parcelProperties]="parcel().properties"
          [parcelStatus]="parcelStatus()"
        ></lib-parcel-property-table>
      </div>
      <div class="grid-view">
        @for (property of parcel().properties; let j = $index; track j) {
          <lib-parcel-property-grid
            [parcelProperty]="property"
            [index]="j"
            [parcelStatus]="parcelStatus()"
          ></lib-parcel-property-grid>
        }
      </div>
    </div>

    <ng-content />
  `,
  styles: `
    @use 'shared' as *;

    @include identify-grid();

    .properties-container {
      container-type: inline-size;

      .table-view {
        display: none;
      }

      .grid-view {
        display: block;
        padding-left: 8px;
        border-left: 1px solid gray;
        background-color: var(--property-bg-color);
      }

      @container (width > 768px) {
        .table-view {
          display: block;
        }

        .grid-view {
          display: none;
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParcelDisplayComponent {
  parcel = input.required<ParcelsWithProperties>();
  prefix = input('');

  readonly searchTerm = input<string>('');

  readonly CROWN_DESC_TYPE = '14';
  private readonly transformParcelStatusPipe = new TransformParcelStatusPipe();
  private readonly disallowedValuesPipe = new DisallowedValuesPipe();

  planningZone = computed(() => {
    const zones = this.parcel().planning_zone;
    if (!zones) return null;

    return unique(zones.split('<br>')).join('\n');
  });

  planningOverlay = computed(() => {
    const overlays = this.parcel().planning_overlay;
    if (!overlays) return null;

    return unique(overlays.split('<br>')).join('\n');
  });

  parcelStatus = computed(() => {
    const parcelStatus = this.parcel().parcel_status;

    const isAllowed = this.disallowedValuesPipe.transform(parcelStatus, [
      'N',
      null,
      '',
      'A',
    ]);
    if (!isAllowed) {
      return '';
    }

    const transformedValue =
      this.transformParcelStatusPipe.transform(parcelStatus);
    if (isEmptyInputStr(transformedValue)) {
      return '';
    }
    return ` (${transformedValue}) `;
  });

  displayedSpi = computed(() => {
    const text = this.parcel().spi;
    const searchTerm = this.searchTerm();

    if (!text || !searchTerm) return text ?? '';

    const term = searchTerm.toLowerCase();
    const spiLower = text.toLowerCase();
    const index = spiLower.indexOf(term);

    if (index !== -1) {
      const beforeMatch = text.substring(0, index);
      const matchedPart = text.substring(index, index + searchTerm.length);
      const afterMatch = text.substring(index + searchTerm.length);
      return `${beforeMatch}<mark>${matchedPart}</mark>${afterMatch}`;
    }

    return text;
  });
}
